import { watch } from "vue";

import { useWelcomeFlowStore } from "@/stores/welcomeFlow/welcomeFlow.store";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { useFlowGuideCoordinationStore } from "@/stores/flowGuide/flowGuideCoordination.store";
import { waitForHasuraAuthenticated } from "@/router/router";
import { HasuraNotAuthenticatedError } from "@/stores/hasura/hasuraErrors";
import { useIsDesktop } from "@/composables/useIsDesktop";

import { WELCOME_RELEASE_DATE } from "./welcomeFlow.config";
import { resolveWelcomeVariant } from "./resolveWelcomeVariant";
import {
  insertWelcomeFlowRow,
  isWelcomeFlowDone,
  recordWelcomeFlowEvent,
  reportSwallowedFailure,
  waitForModalStackToEmpty,
} from "./welcomeFlowShared";
import type { WelcomeDeckWithFlowKey } from "./decks/WelcomeDeck";

type DeckWelcomeFlowConfig = {
  deck: WelcomeDeckWithFlowKey;
  // Resolves once the deck's own anchors exist in the DOM (e.g. useProjectDashboardReadySignal.ts).
  waitForReady: () => Promise<void>;
  // Deck-specific extras for openTour (e.g. the project deck's afterCreation handoff), resolved only
  // once the tour is confirmed to actually open — never preemptively.
  resolveOpenTourOptions?: () => { afterCreation?: boolean };
};

/**
 * One trigger per deck: the guards (desktop, auth, ready, no other tour, empty modal stack, once per
 * session) and the seen-row cache are identical across decks — only the deck itself, its readiness
 * signal, and any deck-specific openTour extras differ.
 */
export function createDeckWelcomeFlow({ deck, waitForReady, resolveOpenTourOptions }: DeckWelcomeFlowConfig) {
  let checked = false;
  let pendingPromise: Promise<boolean> | null = null;

  function queryPending(): Promise<boolean> {
    pendingPromise ??= isWelcomeFlowDone(deck.flowKey, deck.mandatory === true)
      .then((done) => !done)
      .catch((error: unknown) => {
        reportSwallowedFailure(`${deck.id} deck queryPending`, error);
        return false;
      });
    return pendingPromise;
  }

  // Belt-and-braces: another deck's tour normally unregisters itself before this ever runs.
  async function waitForNoActiveWelcomeTour(): Promise<void> {
    const coordinationStore = useFlowGuideCoordinationStore();
    if (!coordinationStore.isWelcomeTourActive) return;

    await new Promise<void>((resolve) => {
      const stop = watch(
        () => coordinationStore.isWelcomeTourActive,
        (active) => {
          if (!active) {
            stop();
            resolve();
          }
        }
      );
    });
  }

  /** Would opening this deck's page right now show its tour? */
  function isPending(): Promise<boolean> {
    return queryPending();
  }

  async function checkAndShow(errorContext?: Record<string, unknown>): Promise<void> {
    if (checked) return;

    // The anchors this tour targets don't render below the desktop breakpoint.
    if (!useIsDesktop().value) return;
    checked = true;

    try {
      await waitForHasuraAuthenticated();
      await waitForReady();
      await waitForNoActiveWelcomeTour();
      await waitForModalStackToEmpty();

      const pending = await queryPending();
      if (!pending) return;

      const authStateStore = useAuthStateStore();
      const variant = resolveWelcomeVariant(authStateStore.user?.createdAt, WELCOME_RELEASE_DATE);

      await insertWelcomeFlowRow(deck.flowKey, variant);
      void recordWelcomeFlowEvent({ flowKey: deck.flowKey, event: "opened", variant });
      pendingPromise = Promise.resolve(false);

      useWelcomeFlowStore().openTour(deck.id, variant, resolveOpenTourOptions?.());
    } catch (error) {
      if (error instanceof HasuraNotAuthenticatedError) return;
      reportSwallowedFailure(`${deck.id} deck checkAndShow`, error, errorContext);
    }
  }

  return { checkAndShow, isPending };
}
